import { useEffect, useRef } from 'react';
import { supabase, useAuth } from '../../supabaseClient';
import Cookies from 'js-cookie';
import { storeChatHistory, getChatHistoy } from '../../chatHistory';


let heartbeatInterval = null;
let visibilityChangeHandler = null;
let pollingInterval = null; // Background polling interval

// Helper function to check browser notification permission
export const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
        console.log('This browser does not support desktop notifications');
        return 'denied';
    }

    if (Notification.permission === 'granted') {
        return 'granted';
    }

    if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        return permission;
    }

    return 'denied';
};

// Helper function to show browser notification
export const showBrowserNotification = (title, message, callback = null) => {
    if (Notification.permission === 'granted') {
        const notification = new Notification(title, {
            body: message,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: 'zoomey-notification', // Prevents duplicate notifications
            requireInteraction: true,
        });

        notification.onclick = () => {
            window.focus();
            notification.close();
            if (callback) callback();
        };

        // Play notification sound
        playNotificationSound();

        return notification;
    }
    return null;
};

// Helper function to play notification sound
const playNotificationSound = () => {
    try {
        const audio = new Audio('/sounds/notification.mp3');
        audio.volume = 0.3;
        audio.play().catch(error => {
            console.log('Could not play notification sound:', error);
        });
    } catch (error) {
        console.log('Error playing notification sound:', error);
    }
};

// Poll for notifications from the backend (fallback when WebSocket is not connected)
export const startNotificationPolling = (
    session,
    notifications,
    setNotifications,
    showNotification
) => {
    if (pollingInterval) {
        clearInterval(pollingInterval);
    }

    // Poll every 10 minutes as fallback
    pollingInterval = setInterval(async () => {
        if (!session || !session.user) return;

        try {
            // Get recent notifications from backend API
            const token = Cookies.get('access_token');
            const response = await fetch(`${process.env.REACT_APP_ZOOMEY_API_URL}/notifications/recent?limit=20`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success' && data.notifications) {
                    // Check for new unread notifications
                    data.notifications.forEach(notif => {
                        const existing = notifications.find(n => n.id === notif.id);

                        if (!existing && !notif.is_read && notif.alert_popup) {
                            // This is a new unread notification
                            const notificationMessage = notif.message || notif.title;

                            // Show in-app notification
                            showNotification(notif.title, notificationMessage);

                            // Show browser notification
                            showBrowserNotification(notif.title, notificationMessage, () => {
                                window.location.href = '/chat-bot';
                            });
                        }
                    });

                    // Update notifications state
                    setNotifications(data.notifications);
                }
            }
        } catch (error) {
            console.error('Error polling for notifications:', error);
        }
    }, 600000); // 10 minutes = 600000ms
};

export const stopNotificationPolling = () => {
    if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }
};

export const runBrodcasting = (
    userId,
    session,
    location,
    setNotifications,
    showNotification,
    setMessages,
    setSummary,
    setCategoryChannel,
    setListCategoryChannel,
    setNotesCategoryChannel,
    setTaskChannel,
    setListschannel,
    setNotesChannel,
    setSideConvoChannel,
    setNotificationSessionChannel,
    setActiveSideConvoChannel,
    setCustomBotChannel,
    setTaskProfileChannel,
    setListProfileChannel,
    setNotesProfileChannel,
    setChatEventChannel,
    subscriptionRef,
    setResetBroadcasting,
    setQueueResponse,
    setNotificationResponse,
    setMeetingResponse,
    setZoomeyCall,
    setOpenReminder,
    setOpenMeeting,
    setOpenSettings,
    setOpenGrocery,
    setOpenQueue,
    setOpenVoiceHistory,
    setOpenScratchpad,
    setOpenNotification,
    setOpenScheduledTasks,
    setResetSessionBtn,
    setChatSessionActivated

) => {
    if (!session) return;
    console.log('[broadcasting] Setting up subscription for user:', session.user?.id);
    if (!subscriptionRef.current) {
        subscriptionRef.current = supabase
            .channel('public:zoomey_'+Date.now(), {
                config: {
                    presence: {
                        key: userId
                    },
                    broadcast: {
                        ack: false,
                        self: false
                    }
                }
            })
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` }, payload => {
                const newNotification = payload.new;
                // Skip queue completion notifications on web (mobile-only)
                if (newNotification.queuerequestid) return;
                setNotifications(prevState => {
                    if (!prevState.some(notification => notification.id === newNotification.id)) {
                        if (newNotification.is_summary === false && newNotification.alert_popup === true && newNotification.status === false) {
                            showNotification(newNotification.title, newNotification.message);
                        } else if (newNotification.is_summary === true && window.location.pathname == '/chat-bot') {                        
                            storeChatHistory(Cookies.get('chat_session_id'), "<h4>Summary of your Recent Conversation:</h4>" + newNotification.message, "text", false);
                            setMessages(prev => [...prev, {
                                text: "<h4>Summary of your Recent Conversation:</h4>" + newNotification.message,
                                isUser: false,
                                isHtml: true
                            }]); // Ensure this updates the messages

                            setSummary({
                                text: "<h4>Summary of your Recent Conversation:</h4>" + newNotification.message,
                                isUser: false,
                                isHtml: true
                            }); // Ens
                        }
                        return [newNotification, ...prevState];
                    }
                    return prevState;
                });
            }).on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` }, payload => {
                const updatedNotification = payload.new;
                 setNotificationResponse(prev => (isNaN(prev) ? 1 : prev + 1));
                setNotifications(prevState => {
                    const existingNotificationIndex = prevState.findIndex(notification => notification.id === updatedNotification.id);
                    if (existingNotificationIndex === -1) {
                        // Notification is not in the state
                        if (updatedNotification.is_summary === false && updatedNotification.alert_popup === true && updatedNotification.status === false) {
                            showNotification(updatedNotification.title, updatedNotification.message);
                        } else if (updatedNotification.is_summary === true && window.location.pathname == '/chat-bot') {
                            storeChatHistory(Cookies.get('chat_session_id'), "<h3>Summary of your Recent Conversation:</h3>" + updatedNotification.message, "text", false);
                            setMessages(prev => [...prev, {
                                text: "<h3>Summary of your Recent Conversation:</h3>" + updatedNotification.message,
                                isUser: false,
                                isHtml: true
                            }]);
                        }
                        return [updatedNotification, ...prevState];
                    } else {
                        // Notification is already in the state but with a different status
                        if (prevState[existingNotificationIndex].status !== updatedNotification.status) {
                            const updatedState = [...prevState];
                            updatedState[existingNotificationIndex] = updatedNotification;
                            return updatedState;
                        }
                        if (prevState[existingNotificationIndex].is_read !== updatedNotification.is_read) {
                            const updatedState = [...prevState];
                            updatedState[existingNotificationIndex] = updatedNotification;
                            return updatedState;
                        }
                    }

                    return prevState;
                });

            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'notifications'}, async (payload) => {
                    const { data,error } = await supabase
                            .from('notifications')
                            .select('*')
                            .eq('user_id', session.user.id);
                            setNotifications(data)

            })
            // for side sessions (topics and subtopics)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_session', filter: `user_auth=eq.${session.user?.id}` }, payload => {
                console.log('[broadcasting] chat_session INSERT received:', payload.new);
                const sessionType = payload.new?.type;
                
                // Auto-navigate for topics/subtopics when is_call is truthy
                if (payload.new.is_call) {
                    if (sessionType === 'topic' || sessionType === 'sub_topic') {
                        Cookies.set('chat_session_id', payload.new.id)
                        window.location.reload()
                        return // Stop further execution since page is reloading
                    }
                }

                setActiveSideConvoChannel(payload.new.id);

                // Update the appropriate channel based on session type
                if (sessionType === 'topic' || sessionType === 'sub_topic') {
                    console.log('[broadcasting] Topic/Sub-topic created, updating sidebar...');
                    setSideConvoChannel((prev) => prev + 1);
                } else if (sessionType === 'notification') {
                    setNotificationSessionChannel((prev) => prev + 1);
                } else {
                    // Unknown type - refresh both to be safe
                    setSideConvoChannel((prev) => prev + 1);
                    setNotificationSessionChannel((prev) => prev + 1);
                }
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_session', filter: `user_auth=eq.${session.user?.id}` },async (payload) => {
                console.log('[broadcasting] chat_session UPDATE received:', payload.new);
                const sessionType = payload.new?.type;
                let chatSessionId = Cookies.get('chat_session_id')
                
                if(payload.new.web === true) {
                    let valuesession;
                    if(chatSessionId == payload.new.id) {
                        setResetSessionBtn((prev) => {
                            valuesession = prev;
                        });

                        setResetSessionBtn(valuesession + 1);
                    }
                }

                if (payload.new.is_active === false) {
                    if(chatSessionId == payload.new.id) {
                        setChatSessionActivated(false);
                    }
                }else {
                    if(chatSessionId == payload.new.id) {
                        setChatSessionActivated(true);
                    }
                }

                if (payload.new.is_call) {
                    console.log('[broadcasting] is_call detected! Navigating to session:', payload.new.id, payload.new.name);
                    await supabase
                    .from('chat_session')
                    .update({ is_call: false })
                    .eq('id', payload.new.id);
                    Cookies.set('chat_session_id', payload.new.id)
                    console.log('[broadcasting] Cookie set, reloading page...');
                    window.location.reload()
                } else {
                    // Update the appropriate channel based on session type
                    if (sessionType === 'topic' || sessionType === 'sub_topic') {
                        console.log('[broadcasting] Topic/Sub-topic updated, refreshing sidebar...');
                        setSideConvoChannel((prev) => prev + 1);
                    } else if (sessionType === 'notification') {
                        setNotificationSessionChannel((prev) => prev + 1);
                    } else {
                        // Unknown type - refresh both to be safe
                        setSideConvoChannel((prev) => prev + 1);
                        setNotificationSessionChannel((prev) => prev + 1);
                    }
                }

            })

            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'chat_session' }, payload => {
                console.log('[broadcasting] chat_session DELETE received:', payload.old);
                // Note: payload.old may only contain primary key if replica identity is not FULL
                // Refresh both channels to ensure UI updates regardless of session type
                const sessionType = payload.old?.type;
                if (sessionType === 'topic' || sessionType === 'sub_topic') {
                    setSideConvoChannel((prev) => prev + 1);
                } else if (sessionType === 'notification') {
                    setNotificationSessionChannel((prev) => prev + 1);
                } else {
                    // Unknown type or type not available - refresh both to be safe
                    setSideConvoChannel((prev) => prev + 1);
                    setNotificationSessionChannel((prev) => prev + 1);
                }
            })
            // TASKS
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tasks', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setTaskChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tasks', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setTaskChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on(
                'postgres_changes',
                { event: 'DELETE', schema: 'public', table: 'tasks', filter: `user_id=eq.${session.user?.id}` },
                (payload) => {
                    setTaskChannel(prev => (isNaN(prev) ? 1 : prev + 1));
                }
            )
            // Category
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'categories', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'categories', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'categories', filter: `user_id=eq.${session.user?.id}`}, payload => {
                setCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            // notes categories broadcasting
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notes_categories', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setNotesCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notes_categories', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setNotesCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'notes_categories', filter: `user_id=eq.${session.user?.id}`}, payload => {
                setNotesCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            // List cateogories broadcasting
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'lists_categories', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setListCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'lists_categories', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setListCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'lists_categories', filter: `user_id=eq.${session.user?.id}`}, payload => {
                setListCategoryChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            // reqltime notes broadcasting
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notes', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setNotesChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notes', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setNotesChannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on(
                'postgres_changes',
                { event: 'DELETE', schema: 'public', table: 'notes', filter: `user_id=eq.${session.user?.id}` },
                (payload) => {
                    setNotesChannel(prev => (isNaN(prev) ? 1 : prev + 1));
                }
            )
            //  realtime lists broadcasting
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'lists', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setListschannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'lists', filter: `user_id=eq.${session.user?.id}` }, payload => {
                setListschannel(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on(
                'postgres_changes',
                { event: 'DELETE', schema: 'public', table: 'lists', filter: `user_id=eq.${session.user?.id}` },
                (payload) => {
                    setListschannel(prev => (isNaN(prev) ? 1 : prev + 1));
                }
            )
            // realtie custom chat bot session
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'custom_chat_bot', filter: `user_id=eq.${session.user?.id}` }, payload => {
                let value3;
                setCustomBotChannel((prev) => {
                    value3 = prev;
                });

                setCustomBotChannel(value3 + 1);
            })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'custom_chat_bot', filter: `user_id=eq.${session.user?.id}` }, payload => {
                let value4;
                setCustomBotChannel((prev) => {
                    value4 = prev;
                });

                setCustomBotChannel(value4 + 1);
            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'custom_chat_bot'}, payload => {

                let value4;
                setCustomBotChannel((prev) => { 
                    value4 = prev;
                });

                setCustomBotChannel(value4 + 1);
            })
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'queue_request'}, payload => {

                 setQueueResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'queue_request'}, payload => {

                setQueueResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'queue_request'}, payload => {

                setQueueResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })


            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'meeting_summaries'}, payload => {

                 setMeetingResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'meeting_summaries'}, payload => {

                setMeetingResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'meeting_summaries'}, payload => {

                setMeetingResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'meeting_summaries'}, payload => {
                console.log('queue chat insert', payload);
                // let value5;
                // setQueueResponse((prev) => { 
                //     value5 = prev;
                //     console.log('queue chat insert', value5);

                // });

                // setQueueResponse(value5 + 1);
                setMeetingResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'meeting_summaries'}, payload => {
                // console.log('queue chat update', payload);

                // let value5;
                // setQueueResponse((prev) => { 
                //     value5 = prev;
                //     console.log('queue chat update', value5);
                // });

                // setQueueResponse(value5 + 1);
                setMeetingResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'queue_chat'}, payload => {
                console.log('queue chat insert', payload);
                // let value5;
                // setQueueResponse((prev) => { 
                //     value5 = prev;
                //     console.log('queue chat insert', value5);

                // });

                // setQueueResponse(value5 + 1);
                setQueueResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'queue_chat'}, payload => {
                // console.log('queue chat update', payload);

                // let value5;
                // setQueueResponse((prev) => { 
                //     value5 = prev;
                //     console.log('queue chat update', value5);
                // });

                // setQueueResponse(value5 + 1);
                setQueueResponse(prev => (isNaN(prev) ? 1 : prev + 1));
            })

            // .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications'}, payload => {
            //     console.log('notification response', payload);

            // })

            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${session.user?.id}` }, async (payload) => {
                if (payload.new.checklist_open ) {
                    try {
                        await supabase
                        .from('profiles')
                        .update({ checklist_open: false })
                        .eq('id', payload.new.id);
                    } catch (error) {
                        console.error('Error updating checklist_open:', error);
                    }
                    let value4 = 0; // Initialize value4
                    setTaskProfileChannel((prev) => {
                        value4 = prev || 0;
                        return value4 + 1;
                    });

                }

                if (payload.new.list_open) {
                    await supabase
                    .from('profiles')
                    .update({ list_open: false })
                    .eq('id', payload.new.id);
                    let value4 = 0; // Initialize value4
                    setListProfileChannel((prev) => {
                        value4 = prev || 0;
                        return value4 + 1;
                    });
                }

                if (payload.new.notes_open) {
                    await supabase
                    .from('profiles')
                    .update({ notes_open: false })
                    .eq('id', payload.new.id);
                    let value4 = 0; // Initialize value4
                    setNotesProfileChannel((prev) => {
                        value4 = prev || 0;
                        return value4 + 1;
                    });
                }

                if (payload.new.call_complete) {
                    await supabase
                    .from('profiles')
                    .update({ call_complete: false })
                    .eq('id', payload.new.id);

                    setZoomeyCall((prev) => {
                        return prev + 1;
                    });
                }

                if (payload.new.reminder_open) {
                    await supabase
                    .from('profiles')
                    .update({ reminder_open: false })
                    .eq('id', payload.new.id);

                    setOpenReminder((prev) => {
                        return prev + 1;
                    });
                }

                // MEETING NAVIGATION - Navigate to Meetings page
                if (payload.new.meeting_open) {
                    await supabase
                    .from('profiles')
                    .update({ meeting_open: false, active_meeting_id: null })
                    .eq('id', payload.new.id);

                    setOpenMeeting((prev) => {
                        return {
                            trigger: (prev?.trigger || 0) + 1,
                            meetingId: payload.new.active_meeting_id || null
                        };
                    });
                }

                // SETTINGS NAVIGATION - Open Settings
                if (payload.new.settings_open) {
                    await supabase
                    .from('profiles')
                    .update({ settings_open: false })
                    .eq('id', payload.new.id);

                    setOpenSettings((prev) => {
                        return (prev || 0) + 1;
                    });
                }

                // GROCERY NAVIGATION - Open Grocery List
                if (payload.new.grocery_open) {
                    await supabase
                    .from('profiles')
                    .update({ grocery_open: false })
                    .eq('id', payload.new.id);

                    setOpenGrocery((prev) => {
                        return (prev || 0) + 1;
                    });
                }

                // QUEUE NAVIGATION - Open Queue
                if (payload.new.queue_open) {
                    await supabase
                    .from('profiles')
                    .update({ queue_open: false })
                    .eq('id', payload.new.id);

                    setOpenQueue((prev) => {
                        return (prev || 0) + 1;
                    });
                }

                // VOICE HISTORY NAVIGATION - Open Voice Chat History
                if (payload.new.voice_history_open) {
                    await supabase
                    .from('profiles')
                    .update({ voice_history_open: false })
                    .eq('id', payload.new.id);

                    setOpenVoiceHistory((prev) => {
                        return (prev || 0) + 1;
                    });
                }

                // SCRATCHPAD NAVIGATION - Open Scratchpad section
                if (payload.new.scratchpad_open) {
                    await supabase
                    .from('profiles')
                    .update({ scratchpad_open: false })
                    .eq('id', payload.new.id);

                    setOpenScratchpad((prev) => {
                        return (prev || 0) + 1;
                    });
                }

                // NOTIFICATION NAVIGATION - Open Notifications section
                if (payload.new.notification_open) {
                    await supabase
                    .from('profiles')
                    .update({ notification_open: false })
                    .eq('id', payload.new.id);

                    setOpenNotification((prev) => {
                        return (prev || 0) + 1;
                    });
                }

                // SCHEDULED TASKS NAVIGATION - Open Scheduled Tasks modal
                if (payload.new.scheduled_tasks_open) {
                    await supabase
                    .from('profiles')
                    .update({ scheduled_tasks_open: false })
                    .eq('id', payload.new.id);

                    setOpenScheduledTasks((prev) => {
                        return (prev || 0) + 1;
                    });
                }
            })  

            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat', filter: `chat_session_id=eq.${Cookies.get('chat_session_id')}` }, async (payload) => {
                if(payload.new.is_vapi === true) {
                    setChatEventChannel((prev) => {
                        return payload.new;
                    });
                }
            })

            // .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat', filter: `chat_session_id=eq.${Cookies.get('chat_session_id')} ` }, async (payload) => {
            //     setChatEventChannel((prev) => {
            //         return payload.new;
            //     });
            // })

            // Add status change handlers
            // .onError((error) => {
            //     console.error('Channel error:', error)
            // })
            // .onClose(() => {
            //     console.log('Channel closed')
            //     // Implement reconnection logic here if needed
            // })
            // .onJoin((status) => {
            //     console.log('Joined channel with status:', status)
            // })



        .subscribe( async (status) => {
            if (status === 'SUBSCRIBED') {
                console.log('Broadcasting channel subscribed successfully');

                // Track presence to keep connection alive
                await subscriptionRef.current.track({
                    user_id: userId,
                    online_at: new Date().toISOString()
                });

                // Clear existing heartbeat if any
                if (heartbeatInterval) {
                    clearInterval(heartbeatInterval);
                }

                // Send heartbeat every 15 seconds to keep connection alive
                heartbeatInterval = setInterval(async () => {
                    if (subscriptionRef.current && subscriptionRef.current.state === 'joined') {
                        try {
                            await subscriptionRef.current.track({
                                user_id: userId,
                                online_at: new Date().toISOString()
                            });
                        } catch (error) {
                            console.error('Heartbeat failed:', error);
                        }
                    }
                }, 15000);

                // Handle visibility change to maintain connection when tab becomes active
                if (!visibilityChangeHandler) {
                    visibilityChangeHandler = async () => {
                        if (!document.hidden && subscriptionRef.current) {
                            // Tab became visible - ensure connection is alive
                            const state = subscriptionRef.current.state;

                            if (state === 'closed' || state === 'errored') {
                                console.log('Reconnecting broadcasting on tab visible...');
                                let countReset;
                                setResetBroadcasting((prev) => {
                                    countReset = prev;
                                });
                                setResetBroadcasting(countReset + 1);
                            } else if (state === 'joined') {
                                // Refresh presence
                                try {
                                    await subscriptionRef.current.track({
                                        user_id: userId,
                                        online_at: new Date().toISOString()
                                    });
                                } catch (error) {
                                    console.error('Failed to refresh presence:', error);
                                }
                            }
                        }
                    };

                    document.addEventListener('visibilitychange', visibilityChangeHandler);
                }

            } else if (status === 'CLOSED') {
                console.log('Broadcasting channel closed');
                subscriptionRef.current = null;

                // Clear heartbeat
                if (heartbeatInterval) {
                    clearInterval(heartbeatInterval);
                    heartbeatInterval = null;
                }

                // Trigger reconnection
                let countReset;
                setResetBroadcasting((prev) => {
                    countReset = prev;
                });
                setResetBroadcasting(countReset + 1);
            } else if (status === 'CHANNEL_ERROR') {
                console.error('Broadcasting channel error');
            }
        });

    }

    return subscriptionRef.current;
};

// Cleanup function to be called when component unmounts
export const cleanupBroadcasting = (subscriptionRef) => {
    if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
    }

    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
    }

    if (visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
        visibilityChangeHandler = null;
    }
};


